import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  COMPARISON_PAGE_SIZE,
  fetchPeerConsensusRating,
  fetchPeerNetIncome,
  fetchPeerRecurringProfit,
  fetchPeerRevenue,
} from '@apis/comparison';
import type {
  ComparisonPeerList,
  PeerConsensusRatingApiRow,
  PeerMetricApiRow,
} from '@app-types/comparisonApi';
import { getAccessToken } from '@utils/authStorage';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';

export type ComparisonApiTabKey =
  | 'rating'
  | 'revenue'
  | 'ordinaryProfit'
  | 'netIncome';

export type ComparisonPeersQueryData =
  | ComparisonPeerList<PeerConsensusRatingApiRow>
  | ComparisonPeerList<PeerMetricApiRow>;

const isComparisonApiTabKey = (value: string): value is ComparisonApiTabKey =>
  value === 'rating' ||
  value === 'revenue' ||
  value === 'ordinaryProfit' ||
  value === 'netIncome';

const isPositiveNumber = (value: number): boolean =>
  Number.isFinite(value) && value > 0;

const fetchByTab = (
  tabKey: ComparisonApiTabKey,
  params: { ticker: string; mkt: string; page: number; pageSize: number },
): Promise<ComparisonPeersQueryData> => {
  switch (tabKey) {
    case 'rating':
      return fetchPeerConsensusRating(params);
    case 'revenue':
      return fetchPeerRevenue(params);
    case 'ordinaryProfit':
      return fetchPeerRecurringProfit(params);
    case 'netIncome':
      return fetchPeerNetIncome(params);
    default:
      return fetchPeerConsensusRating(params);
  }
};

/**
 * React Query hook for the Comparison page. The confirmed contract sends the
 * current URL `ticker` together with `mkt`, `page`, and `pageSize` to every
 * Comparison endpoint. The frontend renders the returned peer list as-is and
 * does not filter it to the current ticker.
 */
export const useComparisonPeers = (
  tabKey: ComparisonApiTabKey,
  page: number,
  pageSize = COMPARISON_PAGE_SIZE,
) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const hasToken = Boolean(getAccessToken());
  const hasValidTab = isComparisonApiTabKey(tabKey);
  const hasValidPage = isPositiveNumber(page);
  const hasValidPageSize = isPositiveNumber(pageSize);

  return useQuery<ComparisonPeersQueryData>({
    queryKey: ['comparison', tabKey, ticker, mkt, page, pageSize],
    queryFn: () => fetchByTab(tabKey, {
      ticker: ticker as string,
      mkt: mkt as string,
      page,
      pageSize,
    }),
    enabled: Boolean(
      ticker && mkt && hasToken && hasValidTab && hasValidPage && hasValidPageSize,
    ),
    staleTime: 60_000,
    retry: 1,
  });
};
